import { useState, useRef, useEffect, useMemo } from "react";
import { Search, X, Play, Pause } from "lucide-react";
import type { Track } from "@/hooks/useTracks";
import { albumLabel } from "@/lib/albumLabels";

interface TrackSearchProps {
  tracks: Track[];
  currentId: string | null;
  playing: boolean;
  onPlay: (id: string) => void;
}

export default function TrackSearch({ tracks, currentId, playing, onPlay }: TrackSearchProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const trimmed = query.trim().toLowerCase();

  const results = useMemo(() => {
    if (!trimmed) return [];
    return tracks.filter((t) => {
      const haystack = `${t.title} ${t.author} ${t.language} ${t.dedicate_to} ${albumLabel(t.album_id)}`.toLowerCase();
      return trimmed.split(/\s+/).every((word) => haystack.includes(word));
    });
  }, [trimmed, tracks]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setOpen(false); setQuery(""); }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  const showResults = open && trimmed.length > 0;

  return (
    <div ref={containerRef} className="relative w-full max-w-sm">
      {/* Search input */}
      <div className="flex items-center gap-2 border border-border bg-card/60 backdrop-blur-sm px-3 py-2 transition-colors focus-within:border-absa-red/50">
        <Search className="w-3.5 h-3.5 text-muted-foreground/60 flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search tracks, artists, language…"
          className="flex-1 bg-transparent text-[11px] font-sans tracking-wide text-foreground placeholder:text-muted-foreground/40 outline-none"
        />
        {query && (
          <button
            onClick={() => { setQuery(""); inputRef.current?.focus(); }}
            className="text-muted-foreground/50 hover:text-foreground transition-colors"
            aria-label="Clear search"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Results dropdown */}
      {showResults && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 border border-border bg-card/95 backdrop-blur-md shadow-lg max-h-[320px] overflow-y-auto">
          {results.length === 0 ? (
            <p className="text-[10px] text-muted-foreground/50 text-center py-6 font-sans tracking-wide">
              No tracks found
            </p>
          ) : (
            <>
              <p className="t-meta text-muted-foreground/40 px-3 pt-2 pb-1">
                {results.length} result{results.length !== 1 ? "s" : ""}
              </p>
              {results.map((track) => {
                const isActive = currentId === track.id;
                const isPlaying = isActive && playing;
                return (
                  <button
                    key={track.id}
                    onClick={() => { onPlay(track.id); setOpen(false); setQuery(""); }}
                    className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors hover:bg-foreground/5 ${
                      isActive ? "bg-absa-red/5" : ""
                    }`}
                  >
                    {/* Play indicator */}
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isPlaying ? "bg-absa-red text-primary-foreground" : "border border-border text-muted-foreground/60"
                    }`}>
                      {isPlaying ? (
                        <Pause className="w-2.5 h-2.5" />
                      ) : (
                        <Play className="w-2.5 h-2.5 ml-px" />
                      )}
                    </div>

                    {/* Track info */}
                    <div className="flex-1 min-w-0">
                      <p className={`font-serif text-[11px] leading-snug truncate ${
                        isActive ? "text-foreground" : "text-foreground/80"
                      }`}>
                        "{track.title}"
                      </p>
                      <p className="t-meta text-muted-foreground/60 truncate">
                        {track.author}
                        {track.dedicate_to ? ` · ${track.dedicate_to}` : ""}
                        {" · "}{track.language}
                      </p>
                    </div>

                    {/* Album badge */}
                    <span className="text-[8px] font-sans uppercase tracking-[0.12em] text-muted-foreground/30 flex-shrink-0 max-w-[80px] truncate">
                      {albumLabel(track.album_id)}
                    </span>
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
